import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery, Slice } from "gatsby";
import { useLocation } from "@reach/router";
import { Helmet } from "react-helmet";
import { SEOConnector } from "../../connectors/SEOConnector";
import { getCookie, setLanguageCookie } from "../../utils";
import { SocialMetaConnector } from "../SocialMeta";
import { GeolocationRedirectModal } from "./GeolocationRedirectModal";
import { CookieAcceptanceBanner } from "../CookieAcceptanceBanner";
import * as styles from "./BaseLayout.module.scss";

const BaseLayout = ({
	children,
	location,
	pageType,
	pageContext,
	seo,
	altLangPages,
	headerNavigation,
	footer,
	alerts,
	hideCookieBanner,
}) => {
	const { site } = useStaticQuery(graphql`
		{
			site {
				siteMetadata {
					siteUrl
				}
			}
		}
	`);
	const { siteUrl } = site.siteMetadata;
	const lang = pageContext?.locale.substring(0, 2).toLowerCase();

	const { pathname } = useLocation();

	const currentPage = altLangPages?.edges.find(
		page =>
			page.node?.context?.locale?.substring(0, 2).toLowerCase() ===
			pageContext.locale?.substring(0, 2)?.toLowerCase()
	);
	const pageUrl = `${siteUrl}${currentPage?.node?.path}`;

	const englishPage = altLangPages?.edges.find(
		page => page.node?.context?.locale?.substring(0, 2).toLowerCase() === "en"
	);
	const englishPageUrl = `${siteUrl}${englishPage?.node?.path}`;

	useEffect(() => {
		// Set language cookie if it doesn't exist
		if (getCookie("nf_lang") !== lang) {
			setLanguageCookie(lang);
		}
	}, [lang]);

	const handleCookieBannerOnload = () => {
		document.body.classList.add(styles.cookieBanner);
	};

	const handleCookieBannerOnUnload = () => {
		document.body.classList.remove(styles.cookieBanner);
	};

	return (
		<div className="page">
			<Helmet
				htmlAttributes={{
					lang: pageContext?.locale ? pageContext.locale.split("-")[0] : "en",
					pagetype: pageType || "undefined",
				}}>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1" />
				<meta httpEquiv="X-UA-Compatible" content="IE=edge" />
				<meta name="referrer" content="no-referrer" />
				<meta name="rating" content="general" />
				<meta name="distribution" content="global" />
				<meta name="classification" content="travel" />
				{/* Default Title if page does not have SEO setup */}
				<title>{process.env.GATSBY_SITE_NAME}</title>
				<link rel="icon" href="/favicon.ico" />
				<link
					rel="apple-touch-icon"
					type="image/png"
					sizes="180x180"
					href="/apple-touch-icon.png"
				/>
				<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
				<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
				<link rel="manifest" href="/site.webmanifest" />
				<link rel="mask-icon" href="/safari-pinned-tab.svg" color="#00AAA6" />
				<meta name="msapplication-TileColor" content="#00AAA6" />
				<meta name="theme-color" content="#ffffff" />

				{!seo && <link rel="canonical" href={pageUrl} />}
				<link rel="alternate" href={englishPageUrl} hrefLang="x-default" />
				{altLangPages?.edges?.map(page => (
					<link
						key={page.node.context.id}
						rel="alternate"
						href={`${siteUrl}${page.node.path}`}
						hrefLang={page.node.context.locale}
					/>
				))}
			</Helmet>
			{seo && <SEOConnector metaData={seo} />}
			{seo && (
				<SocialMetaConnector
					location={location}
					pageUrl={pageUrl}
					metaData={seo}
					locale={pageContext.locale}
				/>
			)}
			<GeolocationRedirectModal />
			<Slice
				alias="header"
				allowEmpty="true"
				pathname={pathname}
				altLangPages={altLangPages}
				pageType={pageType}
				headerNavigation={headerNavigation}
				alerts={alerts}
			/>
			<main className="page__container">{children}</main>
			<Slice alias="footer" allowEmpty="true" altLangPages={altLangPages} footer={footer} />
			<CookieAcceptanceBanner
				onLoad={handleCookieBannerOnload}
				onUnload={handleCookieBannerOnUnload}
				hideCookieBanner={hideCookieBanner}
			/>
		</div>
	);
};

BaseLayout.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	location: PropTypes.object,
	pageType: PropTypes.oneOf([
		"LastMinute",
		"Packages",
		"Flights",
		"Hotels",
		"Cruises",
		"Other",
		"Profile",
	]),
	pageContext: PropTypes.object,
	seo: PropTypes.object,
	altLangPages: PropTypes.shape({
		id: PropTypes.string,
		lang: PropTypes.string,
		path: PropTypes.string,
		edges: PropTypes.array,
	}).isRequired,
	headerNavigation: PropTypes.object,
	footer: PropTypes.object,
	alerts: PropTypes.number.isRequired,
	hideCookieBanner: PropTypes.bool,
};
BaseLayout.defaultProps = {
	location: undefined,
	pageType: "Packages",
	pageContext: undefined,
	seo: undefined,
	headerNavigation: undefined,
	footer: undefined,
	hideCookieBanner: false,
};

export default BaseLayout;
export { BaseLayout };
